import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import ShipmentsMap from '../components/ShipmentsMap';
import { useWebSocket } from '../contexts/WebSocketContext';
import { fetchExistingRoutePlans } from '../utils/api';
import { toast } from 'react-toastify';

export default function CalendarScreen() {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  // Route plans state
  const [routePlans, setRoutePlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  
  // Refs to store promise resolvers/rejecters and current job info
  const promiseResolveRef = useRef(null);
  const promiseRejectRef = useRef(null);
  const currentDateRef = useRef(null);
  
  // Get the WebSocket context directly
  const { addListener } = useWebSocket();
  
  // Fetch existing route plans on component mount
  useEffect(() => {
    fetchRoutePlans();
  }, []);
  
  // Function to fetch route plans from the API
  const fetchRoutePlans = async () => {
    try {
      setLoadingPlans(true);

      const data = await fetchExistingRoutePlans();
      setRoutePlans(data.data);
    } catch (err) {
      console.error('Error fetching route plans:', err);
      toast.error('Error fetching route plans:', err);
    } finally {
      setLoadingPlans(false);
    }
  };
  
  // Set up WebSocket event listener
  useEffect(() => {
    if (!addListener) return;
    
    // Handler for WebSocket messages
    const handleMessage = (data) => {
      // Skip ping/pong messages
      if (data.type === 'ping' || data.type === 'pong') return;
      
      console.log('Webhook message received:', data);
      
      // Process only if we're waiting for results (have resolve/reject functions)
      if (!promiseResolveRef.current || !promiseRejectRef.current) return;
      
      // Check if this is for our current job
      if (data.delivery_date === currentDateRef.current) {
        if (data.error) {
          promiseRejectRef.current(new Error(data.error));
        } else if (data.shipments) {
          promiseResolveRef.current(data);
        } else if (data.status === 'success') {
          promiseResolveRef.current(data);
        }
      } else {
        console.log('Ignoring webhook for different date:', data.delivery_date);
      }
    };
    
    // Add the listener
    const removeListener = addListener(handleMessage);
    
    // Clean up on unmount
    return () => {
      removeListener();
    };
  }, [addListener]); // Only re-run if addListener changes

  // Start the delivery plan process
  const handleStartDeliveryPlan = async () => {
    if (!selectedDate) {
      setError('Please select a date first');
      return;
    }

    // Clear any previous errors
    setError(null);
    setIsLoading(true);
    setJobStatus('starting');

    const formattedDate = selectedDate.toISOString().split('T')[0];
    navigate(`/route-planning?date=${formattedDate}`);
  };

  // Add the Start Scanning function
  const handleStartScanning = () => {
    navigate('/scanning');
  };

  // Handle selecting a route plan
  const handleSelectRoutePlan = (date) => {
    setSelectedDate(new Date(date));
  };

  const navigationButtons = (
    <>
      <button
        onClick={handleStartDeliveryPlan}
        disabled={isLoading}
        className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
          isLoading || !selectedDate 
            ? 'bg-indigo-400 cursor-not-allowed' 
            : 'bg-indigo-600 hover:bg-indigo-700'
        } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
      >
        {isLoading ? 'Processing...' : 'Start Delivery Plan'}
      </button>
      <button
        onClick={handleStartScanning}
        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ml-2"
      >
        Start Scanning
      </button>
      {/* <button className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
        Saved Plans
      </button> */}
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar buttons={navigationButtons} />
      <div className="flex h-[calc(100vh-4rem)]">
        {/* Left side - Date selector and Route Plans */}
        <div className="w-64 bg-white shadow-lg p-4 overflow-y-auto">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Select Date</h2>
          <input
            type="date"
            value={selectedDate ? selectedDate.toISOString().split('T')[0] : ''}
            onChange={(e) => setSelectedDate(new Date(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          />
          {selectedDate && (
            <div className="mt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Selected Date:</h3>
              <p className="text-sm text-gray-600">
                {selectedDate.toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
          )}
          
          {/* Route Plans section */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Route Plans</h2>
            {loadingPlans ? (
              <div className="text-sm text-gray-500 py-2">Loading plans...</div>
            ) : routePlans.length > 0 ? (
              <ul className="space-y-2">
                {routePlans.map((plan) => (
                  <a href={`optimal-routes?delivery_date=${plan.delivery_date}&iteration_id=${plan.iteration_id}`} key={`${plan.delivery_date}-${plan.iteration_id}`}>
                    <li key={`${plan.delivery_date}-${plan.iteration_id}`}>
                      <button
                        onClick={() => handleSelectRoutePlan(plan.delivery_date)}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm ${
                        selectedDate && selectedDate.toISOString().split('T')[0] === plan.delivery_date
                          ? 'bg-indigo-100 text-indigo-700'
                          : 'hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      {new Date(plan.delivery_date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                      {plan.name && (
                        <span className="block text-xs text-gray-500 mt-1">{plan.name}</span>
                      )}
                      </button>
                    </li>
                  </a>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">No saved route plans</p>
            )}
          </div>
          
          {jobStatus === 'started' && (
            <div className="mt-4 p-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-md text-sm">
              Job started successfully. Waiting for completion...
            </div>
          )}
          {jobStatus === 'completed' && (
            <div className="mt-4 p-2 bg-green-50 border border-green-200 text-green-700 rounded-md text-sm">
              Finished getting shipments for {selectedDate.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </div>
          )}
          {error && (
            <div className="mt-4 p-2 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
              {error}
            </div>
          )}
        </div>

        {/* Right side - Map */}
        <div className="flex-1">
          <ShipmentsMap 
            shipments={[]} 
            onWebhookResponse={null}
            selectedDate={selectedDate}
          />
        </div>
      </div>
    </div>
  );
}
